<template>
  <span
    class="icon flex shrink-0"
    :style="style"
    v-html="component"
    :data-name="icon"
    role="presentation"
    aria-hidden="true"
  />
</template>

<script lang="ts">
import { mapKeys } from 'es-toolkit';
import { computed, defineComponent } from 'vue';

import type { PropType } from 'vue';

// Get an object of { [IconName]: filePathWithHash }
const Icons = mapKeys(
  import.meta.glob('../assets/icons/**/*.svg', { eager: true, import: 'default', query: '?raw' }),
  (_, path) => path.match(/\/([^/]+)\.svg$/)![1]!,
) as { [key in string]: `${key}-${string}` };

export const ICON_SIZES = {
  small: '12px',
  medium: '16px',
  semilarge: '20px',
  large: '24px',
  'extra-large': '32px',
} as const;

export type IconSize = keyof typeof ICON_SIZES;

export default defineComponent({
  props: {
    /** Refers to the SVG file name in `@/assets/icons`. */
    icon: {
      type: String,
      required: true,
    },
    /** Apply a defined width/height independently of the icon intrinsic size */
    size: {
      type: String as PropType<IconSize>,
      default: 'medium',
      validator: (size: string) => {
        return Object.keys(ICON_SIZES).includes(size);
      },
    },
  },
  setup(props) {
    const component = computed(() => Icons[props.icon]);

    const style = computed(() => ({
      width: ICON_SIZES[props.size],
      height: ICON_SIZES[props.size],
    }));

    return {
      component,
      style,
    };
  },
});
</script>

<style scoped>
@reference '../assets/main.css';

:deep(svg) {
  overflow: visible;
  width: 100%;
  height: 100%;
}

:deep(svg *) {
  fill: currentColor;
  fill-opacity: 1;
  stroke-opacity: 1;
}

[data-name='child'] :deep(*) {
  fill: transparent;
  stroke: currentColor;
}

[data-name='loader'] {
  @apply animate-spin;
  mask-image: conic-gradient(from 30deg, transparent 10%, black 90%);
}
</style>
